import {
    isPhoneNumberE164,
    PhoneNumberE164,
    ReferenceContactId,
    toPhoneNumberE164,
} from "@sp-crm/core";
import { tierColorForToken } from "components/references/show-reference/tier-colors";
import React from "react";
import { Link } from "react-router-dom";
import {
    AgendaAddContactPhone,
    AgendaCallAction,
    AgendaEmailAction,
    AgendaProvisionPhone,
    AgendaTextAction,
    AgendaTouchpointOverflowMenu,
    AgendaVisitAction,
    contactPath,
} from "./agenda-actions";
import {
    AgendaContactGroup,
    AgendaTouchpoint,
    resolveTouchpointActions,
} from "./marketing-plan-agenda";
import { describeCadence, describeChannels } from "./marketing-plan-format";

interface AgendaContactCardProps {
    group: AgendaContactGroup;
    hasPhoneLine: boolean;
    visitsEnabled: boolean;
    onSendEmail: (contactId: string) => void;
    onChanged: () => void;
}

export const AgendaContactCard: React.FC<AgendaContactCardProps> = ({
    group,
    hasPhoneLine,
    visitsEnabled,
    onSendEmail,
    onChanged,
}) => {
    const color = tierColorForToken(group.tierColor);
    const phone =
        group.contactPhone && isPhoneNumberE164(group.contactPhone)
            ? toPhoneNumberE164(group.contactPhone)
            : null;
    const subtitle = [group.orgName, group.contactEmail ?? group.contactPhone]
        .filter(Boolean)
        .join(" · ");

    return (
        <div className="rounded-lg bg-white p-4 ring-1 ring-gray-300 sm:p-5">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <Link
                        to={contactPath(group.contactId)}
                        className="block truncate font-semibold text-gray-900 hover:underline">
                        {group.contactName}
                    </Link>
                    {subtitle ? (
                        <div className="mt-0.5 truncate text-sm text-gray-500">
                            {subtitle}
                        </div>
                    ) : null}
                </div>
                {group.tierName ? (
                    <span
                        className={`inline-flex flex-shrink-0 items-center rounded-full ${color.bg} ${color.text} px-2.5 py-0.5 text-xs font-medium`}>
                        {group.tierName}
                    </span>
                ) : null}
            </div>
            <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
                {group.touchpoints.map(touchpoint => (
                    <AgendaTouchpointRow
                        key={touchpoint.touchpointId}
                        touchpoint={touchpoint}
                        group={group}
                        phone={phone}
                        hasPhoneLine={hasPhoneLine}
                        visitsEnabled={visitsEnabled}
                        onSendEmail={() => onSendEmail(group.contactId)}
                        onChanged={onChanged}
                    />
                ))}
            </div>
        </div>
    );
};

interface AgendaTouchpointRowProps {
    touchpoint: AgendaTouchpoint;
    group: AgendaContactGroup;
    phone: PhoneNumberE164 | null;
    hasPhoneLine: boolean;
    visitsEnabled: boolean;
    onSendEmail: () => void;
    onChanged: () => void;
}

const AgendaTouchpointRow: React.FC<AgendaTouchpointRowProps> = ({
    touchpoint,
    group,
    phone,
    hasPhoneLine,
    visitsEnabled,
    onSendEmail,
    onChanged,
}) => {
    const actions = resolveTouchpointActions({
        kinds: touchpoint.kinds,
        contactEmail: group.contactEmail,
        contactEmailOptedOut: group.contactEmailOptedOut,
        hasContactPhone: !!phone,
        hasPhoneLine,
        visitsEnabled,
    });

    return (
        <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm">
                <div className="font-medium text-gray-700">
                    {describeChannels(touchpoint.kinds)}{" "}
                    {describeCadence(touchpoint.intervalCount, touchpoint.intervalUnit)}
                </div>
                <div className="text-gray-500">{lastReachedLabel(touchpoint)}</div>
            </div>
            <div className="flex flex-wrap gap-2 sm:justify-end">
                {actions.email ? (
                    <AgendaEmailAction
                        state={actions.email}
                        contactId={group.contactId}
                        onSendEmail={onSendEmail}
                    />
                ) : null}
                {actions.addContactPhone ? (
                    <AgendaAddContactPhone contactId={group.contactId} />
                ) : null}
                {actions.provisionPhoneLine ? <AgendaProvisionPhone /> : null}
                {actions.call && phone ? (
                    <AgendaCallAction
                        phone={phone}
                        entityId={group.contactId as ReferenceContactId}
                    />
                ) : null}
                {actions.text && phone ? <AgendaTextAction phone={phone} /> : null}
                {actions.visit ? (
                    <AgendaVisitAction
                        contactId={group.contactId}
                        contactName={group.contactName}
                        onScheduled={onChanged}
                    />
                ) : null}
                <AgendaTouchpointOverflowMenu
                    contactId={group.contactId}
                    touchpointId={touchpoint.touchpointId}
                    kinds={touchpoint.kinds}
                    onChanged={onChanged}
                />
            </div>
        </div>
    );
};

const lastReachedLabel = (touchpoint: AgendaTouchpoint): string =>
    touchpoint.lastContactedAt
        ? `Last reached ${new Date(touchpoint.lastContactedAt).toLocaleDateString()}`
        : "Not yet reached";
